import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  X, ChevronLeft, ChevronRight, Lightbulb, Target, AlertTriangle, Brain,
  CheckCircle2, AlertCircle, HelpCircle, Trophy, RotateCcw,
} from 'lucide-react';
import { VISUAL_MAP } from './PrerequisVisuals';

const ACCENT = {
  red:     { text: 'text-red-300',     dot: 'bg-red-500',     border: 'border-red-500/50',     btn: 'bg-red-600 hover:bg-red-500' },
  amber:   { text: 'text-amber-300',   dot: 'bg-amber-500',   border: 'border-amber-500/50',   btn: 'bg-amber-600 hover:bg-amber-500' },
  sky:     { text: 'text-sky-300',     dot: 'bg-sky-500',     border: 'border-sky-500/50',     btn: 'bg-sky-600 hover:bg-sky-500' },
  emerald: { text: 'text-emerald-300', dot: 'bg-emerald-500', border: 'border-emerald-500/50', btn: 'bg-emerald-600 hover:bg-emerald-500' },
  violet:  { text: 'text-violet-300',  dot: 'bg-violet-500',  border: 'border-violet-500/50',  btn: 'bg-violet-600 hover:bg-violet-500' },
};

function buildSlides(item) {
  const slides = [
    { type: 'cover' },
    { type: 'para', icon: Lightbulb, title: 'Pourquoi ?', text: item.pourquoi },
    { type: 'list', icon: Target, title: 'Comment ?', items: item.comment },
  ];
  if (item.risques?.length) slides.push({ type: 'list', icon: AlertTriangle, title: 'Risques si non maîtrisé', items: item.risques, danger: true });
  if (item.influences?.length) slides.push({ type: 'list', icon: Brain, title: 'Influences', items: item.influences });
  slides.push({ type: 'list', icon: CheckCircle2, title: 'Points-clés à retenir', items: item.points_cles });
  slides.push({ type: 'list', icon: AlertCircle, title: 'Erreurs classiques', items: item.erreurs, danger: true });
  (item.qcm || []).forEach((q, i) => slides.push({ type: 'qcm', q, num: i + 1, total: item.qcm.length }));
  if (item.qcm?.length) slides.push({ type: 'score' });
  return slides;
}

export default function PrerequisPresentMode({ item, startAt = 'start', onClose }) {
  const slides = useMemo(() => buildSlides(item), [item]);
  const firstQcm = slides.findIndex((s) => s.type === 'qcm');
  const [idx, setIdx] = useState(startAt === 'qcm' && firstQcm >= 0 ? firstQcm : 0);
  const [answers, setAnswers] = useState({});
  const touchX = useRef(null);
  const a = ACCENT[item.accent] || ACCENT.red;
  const Visual = VISUAL_MAP[item.slug];
  const slide = slides[idx];

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.requestFullscreen?.().catch(() => {});
    return () => {
      document.body.style.overflow = '';
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') setIdx((i) => Math.min(i + 1, slides.length - 1));
      if (e.key === 'ArrowLeft') setIdx((i) => Math.max(i - 1, 0));
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [slides.length, onClose]);

  const next = () => setIdx((i) => Math.min(i + 1, slides.length - 1));
  const prev = () => setIdx((i) => Math.max(i - 1, 0));

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (dx < -60) next();
    if (dx > 60) prev();
    touchX.current = null;
  };

  const score = (item.qcm || []).reduce((acc, q, i) => acc + (answers[i] === q.correct ? 1 : 0), 0);

  return (
    <div className="fixed inset-0 z-[200] bg-[#0a0a0b] flex flex-col" data-testid="prerequis-present-mode">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 shrink-0">
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] font-mono-spec uppercase tracking-widest font-bold ${a.text} flex items-center gap-1.5`}>
            <span className={`w-1.5 h-1.5 rounded-full ${a.dot}`} /> {item.kicker}
          </div>
          <div className="text-white font-serif-editorial text-[17px] leading-tight truncate">{item.title}</div>
        </div>
        <span className="text-white/40 text-[12px] font-mono-spec shrink-0">{idx + 1}/{slides.length}</span>
        <button onClick={onClose} data-testid="present-close-btn" className="shrink-0 w-10 h-10 rounded-full border border-white/15 text-white/80 hover:bg-white/10 flex items-center justify-center">
          <X size={18} />
        </button>
      </div>

      {/* Contenu */}
      <div className="flex-1 overflow-y-auto px-5 py-6" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid="present-slide-content">
        <div className="max-w-2xl mx-auto">
          {slide.type === 'cover' && (
            <div className="text-center">
              <h2 className="font-serif-editorial text-white text-[34px] sm:text-[46px] leading-tight mb-3">{item.title}</h2>
              <p className={`${a.text} text-[16px] leading-relaxed mb-6`}>{item.tagline}</p>
              {Visual && <div className={`rounded-2xl border ${a.border} overflow-hidden`}><Visual /></div>}
              <p className="mt-6 text-white/40 text-[13px]">Balaye ou utilise les flèches pour avancer →</p>
            </div>
          )}

          {slide.type === 'para' && (
            <SlideFrame icon={slide.icon} title={slide.title} accent={a}>
              <p className="text-white/90 text-[19px] sm:text-[21px] leading-relaxed">{slide.text}</p>
            </SlideFrame>
          )}

          {slide.type === 'list' && (
            <SlideFrame icon={slide.icon} title={slide.title} accent={a}>
              <ul className="space-y-4">
                {slide.items.map((it, i) => (
                  <li key={i} className="flex gap-3.5 text-white/90 text-[17px] sm:text-[19px] leading-relaxed">
                    {slide.danger
                      ? <span className="shrink-0 mt-0.5 text-red-400 font-bold text-[18px]">✗</span>
                      : <span className={`shrink-0 mt-2.5 w-2 h-2 rounded-full ${a.dot}`} />}
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </SlideFrame>
          )}

          {slide.type === 'qcm' && (
            <QcmSlide
              slide={slide}
              accent={a}
              qIndex={slide.num - 1}
              chosen={answers[slide.num - 1]}
              onChoose={(optIdx) => setAnswers((prev2) => ({ ...prev2, [slide.num - 1]: optIdx }))}
            />
          )}

          {slide.type === 'score' && (
            <div className="text-center pt-8" data-testid="present-score-slide">
              <Trophy size={44} className={`mx-auto mb-4 ${score === item.qcm.length ? 'text-amber-400' : 'text-white/40'}`} />
              <h3 className="font-serif-editorial text-white text-[32px] mb-2">Résultat du QCM</h3>
              <div className={`text-[54px] font-serif-editorial ${score === item.qcm.length ? 'text-emerald-400' : score >= item.qcm.length / 2 ? 'text-amber-300' : 'text-red-400'}`}>
                {score}/{item.qcm.length}
              </div>
              <p className="text-white/60 text-[15px] mt-3 max-w-md mx-auto">
                {score === item.qcm.length
                  ? 'Parfait, le contenu est ancré ! On peut passer au pré-requis suivant.'
                  : 'Reviens sur les questions ratées et ré-explique avec le visuel avant de refaire le QCM.'}
              </p>
              <button
                onClick={() => { setAnswers({}); setIdx(firstQcm); }}
                data-testid="present-qcm-retry"
                className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-full border border-white/20 text-white text-[14px] font-semibold hover:bg-white/10"
              >
                <RotateCcw size={15} /> Refaire le QCM
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Footer navigation */}
      <div className="shrink-0 border-t border-white/10 px-4 py-3 flex items-center gap-3">
        <button onClick={prev} disabled={idx === 0} data-testid="present-prev-btn" className="h-14 flex-1 rounded-2xl border border-white/15 text-white disabled:opacity-25 flex items-center justify-center gap-2 text-[15px] font-semibold active:bg-white/10">
          <ChevronLeft size={20} /> Précédent
        </button>
        <div className="flex items-center gap-1.5 shrink-0">
          {slides.map((_, i) => (
            <span key={i} className={`rounded-full transition-all ${i === idx ? `w-5 h-2 ${a.dot}` : 'w-2 h-2 bg-white/20'}`} />
          ))}
        </div>
        <button onClick={next} disabled={idx === slides.length - 1} data-testid="present-next-btn" className={`h-14 flex-1 rounded-2xl ${a.btn} text-white disabled:opacity-25 flex items-center justify-center gap-2 text-[15px] font-bold`}>
          Suivant <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
}

function SlideFrame({ icon: Icon, title, accent, children }) {
  return (
    <div>
      <div className={`text-[11px] uppercase tracking-[0.22em] font-mono-spec font-bold ${accent.text} mb-5 inline-flex items-center gap-2`}>
        <Icon size={15} /> {title}
      </div>
      {children}
    </div>
  );
}

function QcmSlide({ slide, accent, qIndex, chosen, onChoose }) {
  const answered = chosen !== undefined;
  return (
    <div data-testid={`present-qcm-slide-${qIndex}`}>
      <div className={`text-[11px] uppercase tracking-[0.22em] font-mono-spec font-bold ${accent.text} mb-4 inline-flex items-center gap-2`}>
        <HelpCircle size={15} /> Mini QCM · Question {slide.num}/{slide.total}
      </div>
      <h3 className="text-white text-[20px] sm:text-[23px] font-semibold leading-snug mb-6">{slide.q.q}</h3>
      <div className="space-y-3">
        {slide.q.options.map((opt, i) => {
          const isCorrect = i === slide.q.correct;
          const isChosen = chosen === i;
          let cls = 'border-white/15 text-white/90 active:bg-white/10';
          if (answered) {
            if (isCorrect) cls = 'border-emerald-500 bg-emerald-500/15 text-emerald-200';
            else if (isChosen) cls = 'border-red-500 bg-red-500/15 text-red-200';
            else cls = 'border-white/10 text-white/40';
          }
          return (
            <button
              key={i}
              onClick={() => !answered && onChoose(i)}
              disabled={answered}
              data-testid={`present-qcm-option-${qIndex}-${i}`}
              className={`w-full text-left rounded-2xl border-2 px-5 py-4 text-[16px] leading-snug transition flex items-start gap-3 ${cls}`}
            >
              <span className="shrink-0 font-mono-spec font-bold">{String.fromCharCode(65 + i)}.</span>
              <span className="flex-1">{opt}</span>
              {answered && isCorrect && <CheckCircle2 size={20} className="shrink-0 text-emerald-400 mt-0.5" />}
              {answered && isChosen && !isCorrect && <X size={20} className="shrink-0 text-red-400 mt-0.5" />}
            </button>
          );
        })}
      </div>
      {answered && (
        <div className={`mt-5 rounded-2xl border p-4 text-[14.5px] leading-relaxed ${chosen === slide.q.correct ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-100' : 'border-amber-500/40 bg-amber-500/10 text-amber-100'}`} data-testid={`present-qcm-explain-${qIndex}`}>
          <strong>{chosen === slide.q.correct ? 'Bonne réponse ! ' : 'À retenir : '}</strong>{slide.q.explain}
        </div>
      )}
    </div>
  );
}
